import { useVoidReceipt } from '@/api/receipts'
import { useCloseEcomReceipt } from '@/hooks/useRecallReceipt'
import { usePOSStore } from '@/stores/posStore'
import { useUIStore } from '@/stores/uiStore'

// Void the current working set (cart / bound receipt / selected customer).
// Extracted verbatim from FunctionsTab's `case 'void'` so the Functions tab,
// the mobile Quick Actions sheet (and, later, LeftSidebar) all run ONE void
// implementation. Direct-clear semantics, no PIN, no confirm modal — see
// docs void_flow.md. All state is read via getState() at call time so the
// hook is self-contained and never acts on a stale closure.
export function useVoidCurrentReceipt() {
  const voidReceipt = useVoidReceipt()
  const closeEcomReceipt = useCloseEcomReceipt()
  const { showToast } = useUIStore()

  const voidCurrent = () => {
    const {
      cart: currentCart,
      selectedCustomer,
      currentReceiptId,
      isRefundMode,
      loadedEcomMeta,
      clearReceipt,
    } = usePOSStore.getState()

    // "Nothing to void" only when nothing is set anywhere — including a
    // selected customer. See LeftSidebar.handleVoid for the same guard.
    if (currentCart.length === 0 && !currentReceiptId && !selectedCustomer) {
      showToast({ type: 'warning', title: 'Nothing to Void', message: 'Cart is empty.' })
      return
    }

    // Refund-in-progress: discard the local refund cart, no backend void.
    if (isRefundMode) {
      clearReceipt()
      showToast({ type: 'success', title: 'Refund Cancelled', message: 'Refund has been cancelled.' })
      return
    }

    // Loaded ecom/delivery order: CLOSE (revert to its pre-load status +
    // clear) rather than cancel — the clerk may just be viewing it. Real
    // cancellation stays on the OrderDetailModal "Cancel Order" button.
    if (loadedEcomMeta) {
      ;(async () => {
        try {
          await closeEcomReceipt.close()
          showToast({ type: 'success', title: 'Order Closed', message: 'Order returned to the list. It was not cancelled.' })
        } catch (err) {
          showToast({
            type: 'error',
            title: 'Could not close order',
            message: err instanceof Error ? err.message : undefined,
          })
        }
      })()
      return
    }

    ;(async () => {
      if (currentReceiptId) {
        // Pre-flight: fully-paid receipts can't be voided BE-side
        // ("Cannot void a receipt with payments"). When we already know the
        // balance is zero, skip the round-trip and detach locally — escape
        // hatch for the post-payment stuck state. See LeftSidebar.handleVoid.
        const { serverTotals } = usePOSStore.getState()
        const amountToPay = serverTotals?.amountToPay
        if (amountToPay !== undefined && amountToPay !== null && Math.abs(amountToPay) <= 0.005) {
          clearReceipt()
          showToast({
            type: 'warning',
            title: 'Receipt Already Closed',
            message: "Paid receipts can't be voided. Cart cleared — use Refund/Return for paid orders.",
          })
          return
        }
        try {
          await voidReceipt.mutateAsync({ receiptId: currentReceiptId })
        } catch (err: unknown) {
          const axiosErr = err as { response?: { status?: number; data?: { code?: string; error?: string } } }
          const code = axiosErr?.response?.data?.code
          const errMsg = (axiosErr?.response?.data?.error || (err instanceof Error ? err.message : '')).toLowerCase()

          // Terminal — see LeftSidebar.handleVoid for full context on why
          // HTTP 500 also counts (v2 backend currently 500s on paid-receipt
          // voids instead of returning a structured 409) and why
          // 'with payments' / 'cancel any payments' are treated terminal.
          const status = axiosErr?.response?.status
          const isTerminal =
            status === 409
            || status === 500
            || code === 'cannot_cancel_paid'
            || code === 'already_terminal'
            || code === 'payment_in_progress'
            || errMsg.includes('is locked')
            || errMsg.includes('cannot be modified')
            || errMsg.includes('already paid')
            || errMsg.includes('with payments')
            || errMsg.includes('cancel any payments')

          if (isTerminal) {
            clearReceipt()
            showToast({
              type: 'warning',
              title: 'Receipt Already Closed',
              message:
                code === 'cannot_cancel_paid' || errMsg.includes('already paid') || errMsg.includes('with payments')
                  ? "Paid receipts can't be voided. Cart cleared — use Refund/Return for paid orders."
                  : 'Receipt is closed on the backend. Cart cleared so you can start fresh.',
            })
            return
          }

          const msg = axiosErr?.response?.data?.error || (err instanceof Error ? err.message : 'Void failed.')
          showToast({ type: 'error', title: 'Void Failed', message: msg })
          return
        }
      }
      clearReceipt()
      showToast({ type: 'success', title: 'Receipt Voided', message: 'Transaction has been cancelled.' })
    })()
  }

  return { voidCurrent, isPending: voidReceipt.isPending }
}
